/**
 * Tracking scripts for non-Google providers + consent updates + SPA pageviews.
 * Google Tag Manager / GA4 load early via /api/settings/tracking-bootstrap.js in index.html.
 */

import axios from '../axiosConfig';
import { isCookieAccepted } from './cookieConsent';

const LOADED_ATTR = 'data-mailsball-tracking';

let cachedConfig = null;
let configPromise = null;
let scriptsInitialized = false;

function appendScript(id, src) {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = true;
  script.setAttribute(LOADED_ATTR, 'true');
  script.onerror = () => {
    console.warn(`[Tracking] Failed to load script: ${src}`);
  };
  document.head.appendChild(script);
}

function appendInlineScript(id, content) {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.setAttribute(LOADED_ATTR, 'true');
  script.text = content;
  document.head.appendChild(script);
}

function removeNonGoogleScripts() {
  document.querySelectorAll(`[${LOADED_ATTR}]`).forEach((el) => el.remove());
}

function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
}

/** Sync Google Consent Mode v2 with cookie banner choices. */
export function updateGoogleConsent() {
  ensureGtag();

  const analyticsAllowed = isCookieAccepted('analytics');
  const targetingAllowed = isCookieAccepted('targeting');
  const functionalAllowed = isCookieAccepted('functional');

  window.gtag('consent', 'update', {
    analytics_storage: analyticsAllowed ? 'granted' : 'denied',
    ad_storage: targetingAllowed ? 'granted' : 'denied',
    ad_user_data: targetingAllowed ? 'granted' : 'denied',
    ad_personalization: targetingAllowed ? 'granted' : 'denied',
    functionality_storage: functionalAllowed ? 'granted' : 'denied',
    personalization_storage: functionalAllowed ? 'granted' : 'denied',
  });
}

async function fetchAnalyticsConfig() {
  if (cachedConfig) return cachedConfig;
  if (configPromise) return configPromise;

  configPromise = axios
    .get('/api/settings/analytics')
    .then((res) => {
      cachedConfig = res.data?.analytics || {};
      return cachedConfig;
    })
    .catch((err) => {
      console.warn('[Tracking] Analytics settings unavailable:', err.message || err);
      cachedConfig = {};
      return cachedConfig;
    })
    .finally(() => {
      configPromise = null;
    });

  return configPromise;
}

function loadFacebookPixel(pixelId) {
  appendInlineScript(
    'mailsball-fb-pixel',
    `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${pixelId}');fbq('track','PageView');`
  );
}

function loadYandexMetrika(counterId) {
  appendInlineScript(
    'mailsball-yandex-metrika',
    `(function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};m[i].l=1*new Date();for(var j=0;j<document.scripts.length;j++){if(document.scripts[j].src===r){return;}}k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})(window,document,'script','https://mc.yandex.ru/metrika/tag.js','ym');ym(${counterId},"init",{clickmap:true,trackLinks:true,accurateTrackBounce:true,webvisor:true});`
  );
}

function loadLinkedIn(partnerId) {
  appendInlineScript(
    'mailsball-linkedin-insight',
    `_linkedin_partner_id="${partnerId}";window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];window._linkedin_data_partner_ids.push(_linkedin_partner_id);`
  );
  appendScript(
    'mailsball-linkedin-script',
    'https://snap.licdn.com/li.lms-analytics/insight.min.js'
  );
}

function loadTikTok(pixelId) {
  appendInlineScript(
    'mailsball-tiktok-pixel',
    `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${pixelId}');ttq.page();}(window,document,'ttq');`
  );
}

function loadMicrosoftClarity(projectId) {
  appendInlineScript(
    'mailsball-clarity',
    `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${projectId}");`
  );
}

function loadHotjar(siteId) {
  appendInlineScript(
    'mailsball-hotjar',
    `(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:${siteId},hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`
  );
}

function applyThirdPartyScripts(config) {
  removeNonGoogleScripts();

  const analyticsAllowed = isCookieAccepted('analytics');
  const targetingAllowed = isCookieAccepted('targeting');

  if (analyticsAllowed) {
    if (config.yandexMetrikaId?.trim()) {
      loadYandexMetrika(config.yandexMetrikaId.trim());
    }
    if (config.microsoftClarityId?.trim()) {
      loadMicrosoftClarity(config.microsoftClarityId.trim());
    }
    if (config.hotjarSiteId?.trim()) {
      loadHotjar(config.hotjarSiteId.trim());
    }
  }

  if (targetingAllowed) {
    const fbId = config.facebookPixelId?.trim();
    // Skip if hardcoded pixel in index.html is already active
    if (fbId && !window.fbq) {
      loadFacebookPixel(fbId);
    }
    if (config.linkedInPartnerId?.trim()) {
      loadLinkedIn(config.linkedInPartnerId.trim());
    }
    if (config.tikTokPixelId?.trim()) {
      loadTikTok(config.tikTokPixelId.trim());
    }
  }
}

export async function initTrackingScripts() {
  try {
    const config = await fetchAnalyticsConfig();
    updateGoogleConsent();
    applyThirdPartyScripts(config);
    scriptsInitialized = true;
  } catch (err) {
    console.warn('[Tracking] Init failed:', err);
  }
}

export function refreshTrackingScripts() {
  updateGoogleConsent();
  if (cachedConfig) {
    applyThirdPartyScripts(cachedConfig);
  } else {
    initTrackingScripts();
  }
}

export function trackExternalPageView(path) {
  if (!scriptsInitialized) return;

  const gaId = cachedConfig?.googleAnalyticsId?.trim();

  if (window.gtag && gaId) {
    window.gtag('config', gaId, { page_path: path });
  }

  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'page_view',
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }

  if (window.fbq) {
    window.fbq('track', 'PageView');
  }

  const metrikaId = cachedConfig?.yandexMetrikaId?.trim();
  if (window.ym && metrikaId) {
    window.ym(Number(metrikaId), 'hit', path);
  }

  if (window.ttq) {
    window.ttq.page();
  }
}

export function invalidateTrackingConfig() {
  cachedConfig = null;
  scriptsInitialized = false;
}

const trackingScripts = {
  initTrackingScripts,
  refreshTrackingScripts,
  invalidateTrackingConfig,
  trackExternalPageView,
  updateGoogleConsent,
};

export default trackingScripts;
